import { Box, Button, CircularProgress, FormControlLabel, IconButton, InputAdornment, TextField } from '@mui/material';
import login from 'api/auth/login';
import { IconClose } from 'assets/icons';
import { LockIcon, PersonIcon, VisibleIcon, VisibleOffIcon } from 'components/common/IconCommon';
import { PATH_ROOT } from 'constants/api';
import { TOAST_DEFAULT } from 'constants/toast';
import { useLogin } from 'hooks';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { NavLink, useSearchParams } from 'react-router-dom';
import { toastError, validateDomain } from 'utils/helpers';

const AUTO_LOGIN = ['viettel-cloud'];

const LoginSystem = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [params] = useSearchParams();
    const { handleLoginResponse } = useLogin();
    const [captcha, setCaptcha] = useState('');
    const [showPass, setShowPass] = useState(false);
    const {
        formState: { errors, isSubmitting },
        handleSubmit,
        register,
        setValue,
        watch,
    } = useForm({ mode: 'onChange' });
    const isAutoLogin = AUTO_LOGIN.includes(params.get('autoLogin'));
    const isDisable = isAutoLogin || isSubmitting;
    const domain = validateDomain(params.get('domain'));

    const handleClickShowPass = () => setShowPass(!showPass);
    
    const _handleSubmit = async (data) => {
        try {
            const body = { username: data.username.trim(), password: data.password, domain, captcha: data.captcha };

            const response = await dispatch(login(data)).unwrap();

            handleLoginResponse({
                data: body,
                captcha: captcha,
                setCaptcha: setCaptcha,
                response: response,
            });
        } catch (error) {
            toastError(t(TOAST_DEFAULT.ERROR));
        }
    };

    return (
        <form onSubmit={handleSubmit(_handleSubmit)}>
            <FormControlLabel
                label={t('login-page.title.username')}
                labelPlacement="top"
                control={
                    <TextField
                        {...register('username', {
                            required: 'validate.usernameOrEmailIsRequired',
                            maxLength: { value: 64, message: 'validate.maxText' },
                        })}
                        autoFocus
                        error={!!errors.username}
                        fullWidth
                        helperText={t(errors.username?.message)}
                        placeholder={t('user.placeholder.usr-email')}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <PersonIcon />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    {watch('username') && (
                                        <IconButton
                                            tabIndex={-1}
                                            aria-label="toggle password visibility"
                                            onClick={() =>
                                                setValue('username', '', { shouldValidate: true, shouldDirty: true })
                                            }
                                            sx={{ p: 0.5 }}
                                        >
                                            <IconClose />
                                        </IconButton>
                                    )}
                                </InputAdornment>
                            ),
                        }}
                    />
                }
            />
            <FormControlLabel
                sx={{ mt: 2 }}
                label={t('login-page.title.password')}
                labelPlacement="top"
                control={
                    <TextField
                        {...register('password', {
                            required: 'validate.pwdRequired',
                            minLength: { value: 8, message: 'validate.pwdMinLength' },
                            maxLength: { value: 32, message: 'validate.max32Text' },
                        })}
                        placeholder={t('user.placeholder.pass')}
                        error={!!errors.password}
                        fullWidth
                        helperText={t(errors.password?.message)}
                        type={showPass ? 'text' : 'password'}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LockIcon />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    {watch('password') && (
                                        <IconButton
                                            tabIndex={-1}
                                            aria-label="toggle password visibility"
                                            onClick={() =>
                                                setValue('password', '', { shouldValidate: true, shouldDirty: true })
                                            }
                                            sx={{ p: 0.5 }}
                                        >
                                            <IconClose />
                                        </IconButton>
                                    )}
                                    <IconButton
                                        tabIndex={-1}
                                        aria-label="toggle password visibility"
                                        onClick={handleClickShowPass}
                                        sx={{ p: 0.5 }}
                                    >
                                        {showPass ? (
                                            <VisibleIcon sx={{ fontSize: { xl: '25px', lg: '20px' } }} />
                                        ) : (
                                            <VisibleOffIcon sx={{ fontSize: { xl: '25px', lg: '20px' } }} />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                }
            />
            {captcha && (
                <FormControlLabel
                    sx={{ mt: 2 }}
                    label={t('user.captcha')}
                    labelPlacement="top"
                    control={
                        <Box display={'flex'} justifyContent={'space-between'}>
                            <TextField
                                {...register('captcha', {
                                    required: 'validate.captchaIsRequired',
                                })}
                                sx={{ width: 'auto' }}
                                error={!!errors.captcha}
                                helperText={t(errors.captcha)}
                            />
                            <div className="captcha-svg" dangerouslySetInnerHTML={{ __html: captcha }} />
                        </Box>
                    }
                />
            )}
            <Button disabled={isDisable} type={'submit'} fullWidth sx={{ mt: 3 }}>
                {t('auth.login')} {isSubmitting && <CircularProgress size={16} sx={{ ml: 2 }} />}
            </Button>
            <NavLink
                className="text-link forgot-password-link"
                to={domain ? `${PATH_ROOT}/auth/forgot-password?domain=${domain}` : `${PATH_ROOT}/auth/forgot-password`}
            >
                {t('auth.fotgotPassword')}{' '}
            </NavLink>
        </form>
    );
};

export default LoginSystem;
